import React, { useEffect, useMemo, useState } from 'react';
import {
  Languages, Briefcase, Loader2, Search, Plus, Edit3, Trash2,
  Save, X, Scissors, AlertCircle, Combine, Tag, Mic,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

/**
 * AdminCatalogsPage — manage the two global lookup catalogs (languages,
 * specialties) used everywhere the bulk editor's ChipMultiSelect appears.
 *
 * Supported actions per entry:
 *   • Rename                     → sweeps every interpreter row's array
 *   • Merge multiple into one    → great for fuzzy duplicates
 *   • Delete                     → strips from every interpreter row
 *   • Bulk "split CSV blobs"     → cleans legacy "A, B, C" → ["A","B","C"]
 *
 * Each row shows the usage count so the admin sees the blast radius BEFORE
 * confirming a rename or delete.
 */
const CATALOGS = [
  { id: 'languages', icon: Languages, label: 'Idiomas' },
  { id: 'specialties', icon: Briefcase, label: 'Especialidades' },
  { id: 'modalities', icon: Mic, label: 'Modalidades de interpretación' },
];

export default function AdminCatalogsPage() {
  const [tab, setTab] = useState('languages');
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [blobs, setBlobs] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  // Modals
  const [renaming, setRenaming] = useState(null);     // {old, new}
  const [merging, setMerging] = useState(false);       // boolean
  const [mergeInto, setMergeInto] = useState('');
  const [adding, setAdding] = useState(false);
  const [newValue, setNewValue] = useState('');

  async function load() {
    setLoading(true); setError('');
    try {
      const { data } = await api.get(`/admin/catalogs/${tab}/usage`);
      setCounts(data.counts || {});
      setBlobs(data.csv_blobs || []);
      setSelected(new Set());
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    }
    setLoading(false);
  }
  useEffect(() => { load(); }, [tab]);

  const values = useMemo(
    () => Object.keys(counts).sort((a, b) => a.localeCompare(b, 'es')),
    [counts]
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return values;
    return values.filter((v) => v.toLowerCase().includes(q));
  }, [values, query]);
  const totalDocs = useMemo(
    () => Object.values(counts).reduce((s, n) => Math.max(s, n), 0),
    [counts]
  );

  function toggle(value) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  }

  // ── Actions ────────────────────────────────────────────────────────────
  async function add() {
    const val = newValue.trim();
    if (!val) return;
    setBusy(true);
    try {
      await api.post(`/admin/catalogs/${tab}`, { value: val });
      setAdding(false); setNewValue('');
      setNotice(`Agregado: "${val}"`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    }
    setBusy(false);
  }
  async function rename() {
    if (!renaming) return;
    const old = renaming.old.trim();
    const next = renaming.next.trim();
    if (!next || old === next) { setRenaming(null); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/catalogs/${tab}/rename`, {
        old_value: old, new_value: next,
      });
      setRenaming(null);
      setNotice(`"${old}" → "${next}" · ${data.touched} intérprete(s) actualizado(s)`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    }
    setBusy(false);
  }
  async function remove(value) {
    const used = counts[value] || 0;
    if (!window.confirm(
      used > 0
        ? `Eliminar "${value}" lo quitará de ${used} intérprete(s). ¿Continuar?`
        : `Eliminar "${value}" del catálogo. ¿Continuar?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/catalogs/${tab}/value`, { data: { value } });
      setNotice(`Eliminado: "${value}" · ${data.touched} intérprete(s) actualizado(s)`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    }
    setBusy(false);
  }
  async function mergeRun() {
    const into = mergeInto.trim();
    const sources = [...selected].filter((s) => s !== into);
    if (!into || sources.length === 0) {
      setError('Selecciona al menos un valor y el destino');
      return;
    }
    if (!window.confirm(`Fusionar ${sources.length} valor(es) en "${into}". Esto reescribe los perfiles de intérpretes afectados. ¿Continuar?`)) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/catalogs/${tab}/merge`, {
        from_values: sources, into_value: into,
      });
      setMerging(false); setMergeInto(''); setSelected(new Set());
      setNotice(`Fusionados ${data.merged} valor(es) · ${data.touched} intérprete(s) actualizado(s)`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    }
    setBusy(false);
  }
  async function splitBlobs() {
    if (!window.confirm(
      `Dividir ${blobs.length} valor(es) compuestos (ej. "A, B, C") en entradas individuales en TODOS los intérpretes que las usen. ¿Continuar?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/catalogs/${tab}/split-csv`);
      setNotice(`Dividido en ${data.touched} intérprete(s). Nuevos al catálogo: ${data.added_to_catalog.length}`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    }
    setBusy(false);
  }

  const isLang = tab === 'languages';
  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-catalogs-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-cmic-50 text-cmic-600 flex items-center justify-center">
          <Tag size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800">Catálogos</h1>
          <p className="text-sm text-charcoal-500">
            Administra los idiomas, especialidades y modalidades disponibles para los perfiles de intérpretes.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-200">
        {CATALOGS.map((c) => {
          const Icon = c.icon;
          const active = tab === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setTab(c.id)}
              data-testid={`catalog-tab-${c.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                active ? 'border-cmic-500 text-cmic-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-800'
              }`}
            >
              <Icon size={15} /> {c.label}
            </button>
          );
        })}
      </div>

      {/* Notices */}
      {error && (
        <div className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm flex items-center gap-2" data-testid="catalog-error">
          <AlertCircle size={14} />{error}
          <button onClick={() => setError('')} className="ml-auto"><X size={12} /></button>
        </div>
      )}
      {notice && (
        <div className="mb-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 text-sm flex items-center gap-2" data-testid="catalog-notice">
          {notice}
          <button onClick={() => setNotice('')} className="ml-auto"><X size={12} /></button>
        </div>
      )}
      {blobs.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="catalog-blob-banner">
          <div className="flex items-start gap-3">
            <Scissors size={18} className="text-amber-700 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                Hay {blobs.length} valor(es) "compuestos" en los perfiles
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Ejemplo: "{blobs[0].value}" debería ser {blobs[0].parts.length} entradas distintas, no una sola.
                Al dividir, se actualizan automáticamente todos los perfiles que las usen.
              </p>
              <button
                onClick={splitBlobs}
                disabled={busy}
                data-testid="catalog-split-csv-btn"
                className="mt-3 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              >
                <Scissors size={12} /> Dividir todo automáticamente
              </button>
            </div>
          </div>
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-amber-800 font-medium">Ver los {blobs.length} valor(es) detectados</summary>
            <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto bg-white rounded-lg border border-amber-200 p-3">
              {blobs.map((b, i) => (
                <li key={i} className="font-mono text-[11px] text-amber-900">
                  <span className="text-amber-600">{b.count}×</span> "{b.value}" → {b.parts.map((p, j) => <code key={j} className="bg-amber-100 px-1 rounded mr-1">{p}</code>)}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Buscar ${isLang ? 'idioma' : 'especialidad'}…`}
            data-testid="catalog-search"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
          />
        </div>
        {selected.size > 0 ? (
          <>
            <span className="text-xs text-charcoal-500">{selected.size} seleccionado(s)</span>
            <button
              onClick={() => { setMerging(true); setMergeInto([...selected][0] || ''); }}
              data-testid="catalog-merge-btn"
              className="inline-flex items-center gap-1.5 bg-cmic-600 hover:bg-cmic-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold"
            >
              <Combine size={12} /> Fusionar ({selected.size})
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs text-charcoal-500 hover:text-charcoal-700"
            >
              Limpiar
            </button>
          </>
        ) : (
          <button
            onClick={() => setAdding(true)}
            data-testid="catalog-add-btn"
            className="inline-flex items-center gap-1.5 bg-cmic-600 hover:bg-cmic-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold"
          >
            <Plus size={12} /> Agregar
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={26} /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-charcoal-400">
            {query ? 'Sin resultados.' : `No hay ${isLang ? 'idiomas' : 'especialidades'} aún.`}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="catalog-table">
            <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-charcoal-500">
              <tr>
                <th className="w-8 py-2.5 px-3"></th>
                <th className="py-2.5 px-3 text-left">Valor</th>
                <th className="py-2.5 px-3 text-right w-32">Intérpretes</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((v) => {
                const c = counts[v] || 0;
                const checked = selected.has(v);
                return (
                  <tr key={v} className={`hover:bg-gray-50 ${checked ? 'bg-cmic-50/40' : ''}`} data-testid={`catalog-row-${v}`}>
                    <td className="py-2 px-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(v)}
                        data-testid={`catalog-check-${v}`}
                        className="accent-cmic-600"
                      />
                    </td>
                    <td className="py-2 px-3 font-medium text-charcoal-800">{v}</td>
                    <td className="py-2 px-3 text-right text-charcoal-600 tabular-nums">
                      {c > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-gray-100 text-charcoal-700 text-xs font-medium">
                          {c}
                        </span>
                      ) : (
                        <span className="text-charcoal-300 text-xs italic">sin uso</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={() => setRenaming({ old: v, next: v })}
                        data-testid={`catalog-rename-${v}`}
                        className="p-1.5 rounded text-charcoal-500 hover:text-cmic-700 hover:bg-cmic-50 mr-1"
                        title="Renombrar"
                      >
                        <Edit3 size={13} />
                      </button>
                      <button
                        onClick={() => remove(v)}
                        data-testid={`catalog-delete-${v}`}
                        className="p-1.5 rounded text-charcoal-500 hover:text-rose-700 hover:bg-rose-50"
                        title="Eliminar"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-[11px] text-charcoal-400 mt-3">
        {values.length} entradas · Mayor uso: {totalDocs} intérprete(s)
      </p>

      {/* Add modal */}
      {adding && (
        <Modal title={`Agregar ${isLang ? 'idioma' : 'especialidad'}`} onClose={() => setAdding(false)}>
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            data-testid="catalog-add-input"
            placeholder={isLang ? 'Ej. Catalán' : 'Ej. Energía renovable'}
            autoFocus
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
          <ModalFooter onCancel={() => setAdding(false)} onSubmit={add} disabled={busy || !newValue.trim()} />
        </Modal>
      )}

      {/* Rename modal */}
      {renaming && (
        <Modal title={`Renombrar "${renaming.old}"`} onClose={() => setRenaming(null)}>
          <input
            type="text"
            value={renaming.next}
            onChange={(e) => setRenaming({ ...renaming, next: e.target.value })}
            data-testid="catalog-rename-input"
            autoFocus
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
          <p className="text-[11px] text-charcoal-500 mt-2">
            Esto actualizará "{renaming.old}" en {counts[renaming.old] || 0} perfil(es) de intérprete(s).
          </p>
          <ModalFooter onCancel={() => setRenaming(null)} onSubmit={rename} disabled={busy || !renaming.next.trim()} />
        </Modal>
      )}

      {/* Merge modal */}
      {merging && (
        <Modal title={`Fusionar ${selected.size} valor(es)`} onClose={() => { setMerging(false); setMergeInto(''); }}>
          <p className="text-xs text-charcoal-600 mb-3">
            Se reemplazará cada uno de estos valores por el destino seleccionado:
          </p>
          <ul className="mb-3 max-h-32 overflow-y-auto bg-gray-50 rounded-lg p-2 text-xs">
            {[...selected].map((v) => <li key={v} className="py-0.5">• {v} <span className="text-charcoal-400">({counts[v] || 0} uso)</span></li>)}
          </ul>
          <label className="block text-xs font-medium text-charcoal-700 mb-1.5">Destino (valor final)</label>
          <select
            value={mergeInto}
            onChange={(e) => setMergeInto(e.target.value)}
            data-testid="catalog-merge-into"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            {[...selected].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <ModalFooter onCancel={() => { setMerging(false); setMergeInto(''); }} onSubmit={mergeRun} disabled={busy || !mergeInto} submitLabel="Fusionar" />
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h2 className="font-semibold text-charcoal-800 text-sm">{title}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-charcoal-500"><X size={14} /></button>
        </div>
        <div className="p-5">
          {children}
        </div>
      </div>
    </div>
  );
}

function ModalFooter({ onCancel, onSubmit, disabled, submitLabel = 'Guardar' }) {
  return (
    <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-gray-100">
      <button onClick={onCancel} className="px-3 py-1.5 rounded-lg text-xs font-medium text-charcoal-600 hover:bg-gray-100">Cancelar</button>
      <button
        onClick={onSubmit}
        disabled={disabled}
        data-testid="catalog-modal-submit"
        className="inline-flex items-center gap-1 bg-cmic-600 hover:bg-cmic-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
      >
        <Save size={12} /> {submitLabel}
      </button>
    </div>
  );
}
